import React, { useContext, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useAuth } from "../contexts/AuthContext";
import { TranslationContext } from "../contexts/TranslationContext";
import API_BASE_URL from "../apiConfig";
import Menu from "../components/Menu";

const Main = () => {
  const { translations, routes } = useContext(TranslationContext);
  const { logout } = useAuth();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [followersCount, setFollowersCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem("token"); 
        if (!token) {
          Swal.fire("Error", "User is not authenticated. Please log in.", "error");
          navigate(routes.login);
          return;
        }

        // Fetch user & followers count simultaneously
        const [userRes, followersRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/user/fetch?locale=${localStorage.getItem('selectedLanguage')}`, {
            method: "GET",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          }),
          fetch(`${API_BASE_URL}/api/user/counts`, {
            method: "GET",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          }),
        ]);

        if (userRes.ok && followersRes.ok) {
          const userData = await userRes.json();
          const followersData = await followersRes.json();

          if (isMounted) {
            setUser(userData.data);
            setFollowersCount(followersData.followers_count);
          }
        } else {
          const errorData = await userRes.json();
          Swal.fire("Error", errorData.message || "Failed to fetch user data.", "error");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        Swal.fire("Error", "An unexpected error occurred. Please try again.", "error");
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchUserData();

    return () => {
      isMounted = false;
    };
  }, [navigate]);

  const handleQuit = async (e) => {
    e.preventDefault();
  
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No token found.");
  
      const response = await fetch(`${API_BASE_URL}/api/logout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Logout failed");
      }
  
      Swal.fire({
        toast: true,
        position: "top-end", 
        title: translations.loggedOutSuccess,
        icon: "success",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
  
      localStorage.removeItem("token");
      logout();
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
      Swal.fire("Error", error.message, "error");
      localStorage.removeItem("token");
      navigate("/");
    }
  };
  

  return (
    <>
      <Helmet>
        <title>
          {translations.home}
        </title>
      </Helmet>

      <div className="navbar-header container-fluid px-3 pt-2 mb-5">
        <button
          onClick={handleQuit}
          className="btn btn-light bgprimary border-0 rounded-pill quit-btn oavlite-text-primary px-4"
        >
          {translations.quit}
        </button>
        <div className="text-center pt-3 logo-wrapper">
          <Link to="/" className="logo-link">
            <img
              src="../images/oavlite-black.png"
              className="img-fluid mx-auto text-center d-block d-logo mb-3"
              alt="oavlite"
            />
          </Link>
          <p className="mb-2 fw-500 tagline__1">
            {translations.taglineV1}
          </p>
          
        </div>
        <Menu />
      </div>

      <div className="content-wrapper main-page-wrapper text-center">
        <div className="container">
          <div className="center-content mb-4">
            <h4 className="color-primary">{translations.numberUser}</h4>
            {isLoading ? (
              <p>{translations.loading}</p>
            ) : user ? (
              <h5 className="color-primary mb-3">{user}</h5>
            ) : (
              <p>{translations.noUserFound}</p>
            )}
            <h4 className="color-primary">{translations.numberSubscriber}</h4>
            {isLoading ? <p>{translations.loading}</p> : <h5 className="color-primary">{followersCount}</h5>}
          </div>
        </div>
      </div>

      <footer className="text-center d-footer">
        <p>
          <Link className="color-primary" to={routes.legalNotices}>
            CGU
          </Link>
        </p>
      </footer>
    </>
  );
};

export default Main;
